import { BlogPost, CreatePostInput, UpdatePostInput } from '../models/Post';
import axiosClient from '../lib/api/axiosClient';
import { BlogServiceError } from '../lib/errors/BlogServiceError';

interface ApiPost extends Omit<BlogPost, 'Status' | 'CreatedAt' | 'UpdatedAt'> {
    Status: number;
    CreatedAt: string;
    UpdatedAt: string;
}

// interface ApiResponse<T> {
//     status: 'success' | 'error';
//     data: T;
//     message?: string;
// }

export const blogService = {
    async getPosts(page = 1, limit = 10): Promise<{ posts: BlogPost[], hasMore: boolean }> {
        try {
            const { data } = await axiosClient.get<ApiPost[]>(`/GetPosts?page=${page}&limit=${limit}`);
            console.log('API Response:', data);
            
            const posts = data.map(post => ({
                ...post,
                Status: (post.Status === 1 ? 'published' : 'draft') as 'published' | 'draft',
                CreatedAt: new Date(post.CreatedAt),
                UpdatedAt: new Date(post.UpdatedAt)
            }));
            
            return {
                posts,
                hasMore: posts.length === limit
            };
        } catch (error: any) {
            console.error('BlogService: Error fetching posts:', error);
            throw new BlogServiceError(
                error.response?.data?.message || 'Failed to fetch posts',
                error.response?.status || 500
            );
        }
    },

    async getPost(id: string): Promise<BlogPost> {
        try {
            const { data } = await axiosClient.get<ApiPost>(`/posts/${id}`);
            return {
                ...data,
                Status: data.Status === 1 ? 'published' : 'draft',
                CreatedAt: new Date(data.CreatedAt),
                UpdatedAt: new Date(data.UpdatedAt)
            };
        } catch (error: any) {
            throw new BlogServiceError(
                error.response?.data?.message || `Failed to fetch post: ${id}`,
                error.response?.status || 500
            );
        }
    },

    async createPost(post: CreatePostInput): Promise<BlogPost> {
        try {
            console.log('BlogService: Creating post with data:', post);
            const { data } = await axiosClient.post<ApiPost>('/CreatePost', post);
            console.log('BlogService: Received create response:', data);
            
            return {
                ...data,
                Status: data.Status === 1 ? 'published' : 'draft',
                CreatedAt: new Date(data.CreatedAt),
                UpdatedAt: new Date(data.UpdatedAt)
            };
        } catch (error: any) {
            console.error('BlogService: Error creating post:', error.response || error);
            throw new BlogServiceError(
                error.response?.data?.message || 'Failed to create post',
                error.response?.status || 500
            );
        }
    },

    async updatePost(id: string, updates: UpdatePostInput): Promise<BlogPost> {
        try {
            console.log('BlogService: Updating post:', id, 'with data:', updates);
            const { data } = await axiosClient.put<ApiPost>(`/posts/${id}`, updates);
            console.log('BlogService: Received update response:', data);
            
            return {
                ...data,
                Status: data.Status === 1 ? 'published' : 'draft',
                CreatedAt: new Date(data.CreatedAt),
                UpdatedAt: new Date(data.UpdatedAt)
            };
        } catch (error: any) {
            console.error('BlogService: Error updating post:', error.response || error);
            throw new BlogServiceError(
                error.response?.data?.message || `Failed to update post: ${id}`,
                error.response?.status || 500
            );
        }
    },

    async deletePost(id: string): Promise<void> {
        try {
            await axiosClient.delete(`/posts/${id}`);
        } catch (error: any) {
            throw new BlogServiceError(
                error.response?.data?.message || `Failed to delete post: ${id}`,
                error.response?.status || 500
            );
        }
    },

    async reorderPosts(postIds: string[]): Promise<void> {
        try {
            await axiosClient.post('/posts/reorder', postIds);
        } catch (error: any) {
            throw new BlogServiceError(
                error.response?.data?.message || 'Failed to reorder posts',
                error.response?.status || 500
            );
        }
    }
}; 